// ink-component
import React from 'react';
import styled, { ThemeProvider } from 'styled-components';

import useDeprecatedWarning from '../../hooks/useDeprecatedWarning';
import getGlobalProps from '../../helpers/constants/global-props';

import {
  errorMessage,
  formSectionLegendWrapper,
  formSectionFieldset,
  formSectionWrapper,
  legendTitle,
  legendSubtitle,
} from './styles';

export interface FormSectionProps {
  /** Title of the section */
  title?: any;
  /** Subtitle of the section */
  subtitle?: any;
  /** Contents of the section */
  children?: any;
  /** List of possible validation errors in your form section */
  errors?: string[];
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

export const ErrorMessage = styled.span`
  color: #db0000;
  font-size: 13px;
  font-weight: 500;
  &::before {
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
    content: '\\e131';
    display: inline-block;
    margin-right: 6px;
  }

  ${errorMessage}
`;

const FormSectionLegendWrapper = styled.div`
  ${formSectionLegendWrapper}
`;

const FormSectionFieldset = styled.fieldset`
  ${formSectionFieldset}
`;

export const LegendTitle = styled.legend`
  ${legendTitle}
`;

const LegendSubtitle = styled.span`
  ${legendSubtitle}
`;

export const FormSectionWrapper = styled.div`
  ${formSectionWrapper}
`;

const globalProps = getGlobalProps({ deprecated: true });

/**
 * @categories form
 * @purpose Breaks forms down into small sections that are easier to understand
 * @deprecated This component is deprecated and replaced by [NewFormSection](https://ink.carta.com/components/NewFormSection/)
 * @status deprecated
 * @replacementComponent NewFormSection
 * @dateOfDeletion September 1st, 2023
 */
export function FormSection({ errors = [], ...props }: FormSectionProps) {
  useDeprecatedWarning({ componentName: 'FormSection', substitute: 'NewFormSection' });

  const hasErrors = () => {
    return Boolean(errors.find(err => Boolean(err.length)));
  };

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        formSectionInvalid: hasErrors(),
      })}
    >
      <FormSectionWrapper {...globalProps} data-testid={props['data-testid']}>
        <FormSectionFieldset>
          <FormSectionLegendWrapper>
            <LegendTitle>{props.title}</LegendTitle>
            {errors.map((error, index) => (
              <ErrorMessage key={index} as="div">
                {error}
              </ErrorMessage>
            ))}
            {props.subtitle && <LegendSubtitle>{props.subtitle}</LegendSubtitle>}
          </FormSectionLegendWrapper>
          {props.children}
        </FormSectionFieldset>
      </FormSectionWrapper>
    </ThemeProvider>
  );
}

FormSection.defaultProps = {
  title: undefined,
  subtitle: undefined,
  children: undefined,
  errors: [],
  'data-testid': undefined,
};

FormSection.displayName = 'FormSection';

export default FormSection;
